// System
import { useSnackbar } from 'notistack';
import { useFormState } from 'react-hook-form';
import { BaseSyntheticEvent, useMemo } from 'react';
// Redux
import { useSelector, useDispatch } from 'react-redux';
import {
  setShowCreateProviderConfigForm,
  setShowCancelProviderConfigForm,
} from '@Modal/reducers/providers/createProviderConfig/createProviderConfigSlice';

// Form
import {
  useCreateProviderConfigForm,
  createProviderConfigFormStateDefault,
} from '@Forms/providers/CreateProviderConfigFormContext';

// Types
import { AppDispatch } from 'src/app/store';
import { StoreInterface } from 'src/app/types';
// Graphql
import {
  useCreateProviderConfigMutation,
  GetProviderConfigsDocument,
} from '@Src/generated.graphql';
// Redux
import { useProviderDrawer } from './useProviderDrawer';
import { useConfigsTable } from './useConfigsTable';

export const useCreateProviderConfig = () => {
  // Redux
  const dispatch = useDispatch<AppDispatch>();
  const { selectedProviderData } = useProviderDrawer();
  const { configTableFilters } = useConfigsTable();
  const [createProviderConfig] = useCreateProviderConfigMutation();

  const { showCancelProviderConfigForm, showCreateProviderConfigForm } = useSelector(
    (state: StoreInterface) => state?.createProviderConfig
  );

  // Other Hooks
  const { enqueueSnackbar } = useSnackbar();

  // Handle Form Cancels
  const handleCreateCancel = () => {
    if (isDirty) {
      return dispatch(setShowCancelProviderConfigForm(true));
    } else {
      return dispatch(setShowCreateProviderConfigForm(false));
    }
  };

  const handleCancelTakeMeBack = (action: string) => {
    if (action === 'back') {
      dispatch(setShowCancelProviderConfigForm(false));
      dispatch(setShowCreateProviderConfigForm(true));
    } else {
      reset(createProviderConfigFormStateDefault);
      dispatch(setShowCancelProviderConfigForm(false));
      dispatch(setShowCreateProviderConfigForm(false));
    }
  };

  // Form Controls
  const { control, handleSubmit, reset } = useCreateProviderConfigForm();
  const { isDirty, errors } = useFormState({ control });
  const handleSubmitCreateProviderConfig = useMemo(
    () => (e?: BaseSyntheticEvent) => {
      return handleSubmit(async (formData) => {
        const convertedBetSuggestions = formData.betSuggestions.split(',').map(Number);
        const convertedFormData = {
          ...formData,
          betSuggestions: convertedBetSuggestions,
          providerId: selectedProviderData?.id,
          isRoundable: formData.isRoundable == 'true',
        };
        try {
          await createProviderConfig({
            variables: { input: convertedFormData },
            refetchQueries: [
              {
                query: GetProviderConfigsDocument,
                variables: { id: selectedProviderData?.id, filter: configTableFilters },
              },
            ],
            awaitRefetchQueries: true,
          });

          enqueueSnackbar('Provider config was successfully created', {
            variant: 'success',
            autoHideDuration: 2000,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
          });

          reset(createProviderConfigFormStateDefault);
          dispatch(setShowCreateProviderConfigForm(false));
        } catch (error) {
          enqueueSnackbar(error.message, {
            variant: 'error',
            autoHideDuration: 2000,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
          });
        }
      })(e);
    },
    [createProviderConfig]
  );

  return {
    // Store
    showCancelProviderConfigForm,
    showCreateProviderConfigForm,
    submittable: isDirty && Object.values(errors).length == 0,
    // Actions
    setShowCreateProviderConfigForm,
    setShowCancelProviderConfigForm,
    handleCreateCancel,
    handleCancelTakeMeBack,
    handleSubmitCreateProviderConfig,
  };
};
